import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, Resolve, Router } from '@angular/router';
import { ApiService, PageService, ParserService } from '@gallaghercp/core-frontend';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { CampusType } from '../client/types/campus.type';

@Injectable({
  providedIn: 'root',
})
export class CampusService implements Resolve<Observable<any>> {
  protected parser = inject(ParserService);
  protected pages = inject(PageService);
  protected api = inject(HttpClient);
  protected apiService = inject(ApiService);
  protected router = inject(Router);

  private enabledCampuses!: CampusType[];

  constructor() {
    // Initialize selected campus from localStorage if available
    const storedCampus = localStorage.getItem('selectedCampus');
    if (storedCampus) {
      this._selectedCampus.next(Number(storedCampus));
    }

    // Ensure the x-service-zone cookie is set correctly
    this.checkCampusCookie();
  }

  private _selectedCampus = new BehaviorSubject<number | null>(null); // Default value: null

  selectedCampus$ = this._selectedCampus.asObservable(); // Expose as observable for subscribers

  get selectedCampus(): number | null {
    return this._selectedCampus.value;
  }

  retrieveEnabledCampuses(): Observable<any> {
    return this.api.get('zones').pipe(
      map((zones: any) =>
        zones.map(
          (zone: any) =>
            ({
              zone: zone.id,
              id: zone.id,
              name: zone.name,
              calendars: zone.data.calendars,
            }) as CampusType,
        ),
      ),
      tap((campuses: CampusType[]) => {
        this.enabledCampuses = campuses;
      }),
    );
  }

  getEnabledCampuses(): CampusType[] {
    return this.enabledCampuses;
  }

  getCurrentCampus(): any {
    return this.apiService.getServiceZone();
  }

  resolve(route: ActivatedRouteSnapshot) {
    return of(
      this.enabledCampuses.filter((country: CampusType) => {
        if (!route.url || !route.url[0] || !route.url[0]['path']) {
          return true;
        }
        return country.code === route.url[0]['path'];
      })[0],
    );
  }

  checkCampusCookie(): void {
    // If we have a selected campus, use that value for the cookie
    if (this.selectedCampus) {
      this.setCampusCookie(this.selectedCampus.toString());
      this.pages.setToStorage('x-service-zone', this.selectedCampus.toString());
    }
    // If no selected campus but we have a cookie, use that
    else if (!this.pages.getFromStorage('x-service-zone') && this.parser.getCookie('x-service-zone')) {
      this.pages.setToStorage('x-service-zone', this.parser.getCookie('x-service-zone'));
    }
    // If we have a value in storage, use that
    else if (this.pages.getFromStorage('x-service-zone')) {
      this.setCampusCookie(this.pages.getFromStorage('x-service-zone') as unknown as string);
    }
    // Default to zone 1 if nothing else is available
    else {
      this.setCampusCookie('1');
      this.pages.setToStorage('x-service-zone', '1');
    }
  }

  setCampusCookie(zone: string): void {
    if (String(zone) === '1') {
      this.parser.setCookie('x-service-zone', null, '/');
    } else {
      this.parser.setCookie('x-service-zone', String(zone), '/');
    }

    if (zone == '1') {
      const root = document.documentElement;
      root.style.setProperty('--primary-color', '#c8102e');
    }
    if (zone == '2') {
      const root = document.documentElement;
      root.style.setProperty('--primary-color', '#0078ae');
    }
    if (zone == '3') {
      const root = document.documentElement;
      root.style.setProperty('--primary-color', '#092e6e');
    }
  }

  setSelectedCampus(campus: number): void {
    this._selectedCampus.next(campus);
    localStorage.setItem('selectedCampus', campus.toString());
    this.apiService.setServiceZone(campus); // Update the zone in the zone service.
    // Update the x-service-zone cookie with the selected campus value

    this.router.navigateByUrl('/_tmp', { skipLocationChange: true }).then(() => {
      this.router.navigate(['/']);
    });

    this.setCampusCookie(campus.toString());
  }
}
